import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import Loader from "../Common/Loader";

import {
  addNoteCategoryAction,
  updateNoteCategoryAction,
} from "../../../store/actions/NoteCategoriesAction";

const AddNoteCategories = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { item } = location.state || {};
  const isEditMode = !!item;

  const [name, setName] = useState("");
  const [colour, setColour] = useState("#000000");
  const [foregroundColor, setForegroundColor] = useState("#ffffff");
  const [status, setStatus] = useState(1);
  const [sequence, setSequence] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const statusOptions = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" },
  ];

  // ================= EDIT MODE =================
  useEffect(() => {
    if (isEditMode && item) {
      setName(item.name || "");
      setColour(item.colour || "");
      setForegroundColor(item.forground_color || "#ffffff"); // ✅ NEW
      setStatus(item.is_active ? 1 : 0);
      setSequence(item.sequence || "");
    }
  }, [isEditMode, item]);

  // ================= VALIDATION =================
  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) newErrors.name = "Category name is required";
    if (!sequence || isNaN(sequence) || Number(sequence) < 1) {
      newErrors.sequence = "Sequence must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleFieldChange = (field, value) => {
    if (field === "name") {
      setName(value);
    } else if (field === "colour") {
      setColour(value);
    } else if (field === "sequence") {
      setSequence(value);
    } else if (field === "status") {
      setStatus(value);
    }
    else if (field === "forground_color") {
      setForegroundColor(value);
    }
    clearFieldError(field);
  };

  // ================= SUBMIT =================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    const payload = {
      category_text: name,
      bg_color: colour,
      forground_color: foregroundColor, // ✅ NEW
      category_status: status === 1 ? "Active" : "Inactive",
      sequence_no: sequence,
    };

    try {
      if (isEditMode) {
        await dispatch(
          updateNoteCategoryAction(payload, item.id)
        );
      } else {
        await dispatch(addNoteCategoryAction(payload));
      }

      navigate("/note-categories");
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="row">
      <div className="col-xl-12">
        {/* BACK */}
        <div className="d-flex justify-content-end mb-3">
          <Link to="/note-categories">
            <Button variant="primary">Back</Button>
          </Link>
        </div>

        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              {/* NAME */}
              <div className="mb-3">
                <label className="form-label">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => handleFieldChange("name", e.target.value)}
                />
                {errors.name && (
                  <div className="text-danger">{errors.name}</div>
                )}
              </div>

              {/* COLOUR */}
              <div className="mb-3">
                <label className="form-label">
                  Background Colour <span className="text-danger">*</span>
                </label>
                <input
                  type="color"
                  value={colour}
                  onChange={(e) => handleFieldChange("colour", e.target.value)}
                  className="form-control"
                  style={{ width: "100px", height: "45px" }}
                />
                {errors.colour && (
                  <div className="text-danger">{errors.colour}</div>
                )}
              </div>

              {/* FOREGROUND COLOUR */}
              <div className="mb-3">
                <label className="form-label">
                  Text Colour <span className="text-danger">*</span>
                </label>
                <input
                  type="color"
                  value={foregroundColor}
                  onChange={(e) =>
                    handleFieldChange("forground_color", e.target.value)
                  }
                  className="form-control"
                  style={{ width: "100px", height: "45px" }}
                />
              </div>

              {/* SEQUENCE */}
              <div className="mb-3">
                <label className="form-label">
                  Sequence <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={sequence}
                  min={1}
                  step={1}
                  onChange={(e) => handleFieldChange("sequence", e.target.value)}
                />
                {errors.sequence && (
                  <div className="text-danger">{errors.sequence}</div>
                )}
              </div>

              {/* STATUS */}
              <div className="mb-3">
                <label className="form-label">
                  Status
                </label>

                <Select
                  options={statusOptions}
                  value={statusOptions.find((opt) => opt.value === status)}
                  onChange={(selectedOption) =>
                    handleFieldChange("status", selectedOption ? selectedOption.value : "")
                  }
                  placeholder="Select Status"
                  isClearable
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "50px",
                      height: "50px",
                      borderColor: errors.status
                        ? "#dc3545"
                        : provided.borderColor,
                      boxShadow: errors.status
                        ? "0 0 0 0.2rem rgba(220,53,69,.25)"
                        : provided.boxShadow,
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      lineHeight: "50px",
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      padding: "4px",
                    }),
                    indicatorSeparator: () => ({
                      display: "none",
                    }),
                  }}
                />

                {errors.status && (
                  <small className="text-danger">{errors.status}</small>
                )}
              </div>



              {/* SUBMIT */}
              <button className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <Loader size="sm" />{" "}
                    {isEditMode ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>{isEditMode ? "Update Category" : "Add Category"}</>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNoteCategories;
